import { useState, useRef, useEffect } from 'react';
import { addTaskComment, getTaskComments, updateTaskComment, deleteTaskComment } from 'services/CaseServices';
import { getTenantIdFromLS } from 'utils/localStorageUtils';
import { confirmDialog } from 'primereact/confirmdialog';

const tenantId = getTenantIdFromLS();

/**
 * Custom hook for managing task comments with pagination, CRUD operations, and infinite scrolling
 *
 * @param {string} taskId - The ID of the task to fetch/manage comments for
 * @param {string|null} caseId - The ID of the case associated with the task (optional)
 * @param {object} userContext - The user context containing user details for permission checks and user info
 * @returns {object} Object containing comments state and functions to manage comments
 */
const useComments = (taskId, caseId, userContext) => {
  const [comments, setComments] = useState([]);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [isDeletingComment, setIsDeletingComment] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false);
  const commentsListRef = useRef(null);

  /**
   * Fetches comments for the task with optional pagination
   *
   * @param {string|null} paginationToken - Token for fetching the next page of comments
   * @returns {Promise<Array>} Promise resolving to the fetched comments
   */
  const fetchComments = (paginationToken = null) => {
    if (!paginationToken) {
      setIsLoadingComments(true);
    } else {
      setIsLoadingMoreComments(true);
    }

    return getTaskComments(taskId, paginationToken)
      .then((response) => {
        if (response?.data?.task_comments) {
          // Format the comments from the API response
          const formattedComments = response.data.task_comments.map((comment) => ({
            id: comment.comment_id,
            text: comment.comment,
            user: {
              name: comment._first_name_ + ' ' + comment._last_name_ || 'Unknown User',
              avatar: comment.profile_image || `${tenantId}/${comment.firm_user_id}/profile_image`,
            },
            time: comment.created_at,
            editable: comment.created_by === userContext?.userDetails?.firm_user_id,
          }));

          // Save the next token for pagination
          setNextToken(response.data.next_token || null);

          // Append new comments to existing ones if paginating
          if (paginationToken) {
            setComments((prevComments) => [...prevComments, ...formattedComments]);
          } else {
            setComments(formattedComments);
          }

          return formattedComments;
        }
        return [];
      })
      .catch((error) => {
        console.error('Error fetching comments:', error);
        return [];
      })
      .finally(() => {
        if (!paginationToken) {
          setIsLoadingComments(false);
        } else {
          setIsLoadingMoreComments(false);
        }
      });
  };

  /**
   * Adds a new comment to the task
   *
   * @param {string} commentText - The text content of the comment to add
   * @returns {Promise<object>} Promise resolving to the created comment object
   * @throws {Error} If the comment text is empty or the API call fails
   */
  const addComment = (commentText) => {
    if (!commentText.trim()) return Promise.reject('Comment text is empty');

    setIsAddingComment(true);

    const commentData = {
      comment: commentText,
      case_id: caseId || null,
    };

    return addTaskComment(taskId, commentData)
      .then((response) => {
        // Create the new comment object
        const comment = {
          id: response.data?.comment_id || Date.now(),
          text: commentText,
          user: {
            name: userContext?.userDetails?.first_name + ' ' + userContext?.userDetails?.last_name,
            avatar: `${tenantId}/${userContext?.userDetails?.firm_user_id}/profile_image`,
          },
          time: new Date(),
          editable: true,
        };

        // Add the new comment to the state
        setComments((prevComments) => [comment, ...prevComments]);
        return comment;
      })
      .catch((error) => {
        console.error('Error adding comment:', error);
        throw error;
      })
      .finally(() => {
        setIsAddingComment(false);
      });
  };

  /**
   * Sets up the editing state for a comment
   *
   * @param {string} id - The ID of the comment to edit
   * @param {string} text - The current text of the comment
   */
  const startEditing = (id, text) => {
    setEditingComment(id);
    setEditCommentText(text);
  };

  /**
   * Cancels the current comment editing operation
   */
  const cancelEditing = () => {
    setEditingComment(null);
    setEditCommentText('');
  };

  /**
   * Updates an existing comment with new text
   *
   * @param {string} id - The ID of the comment to update
   * @returns {Promise<string>} Promise resolving to the ID of the updated comment
   * @throws {Error} If the comment text is empty or the API call fails
   */
  const updateComment = (id) => {
    if (!editCommentText.trim()) return Promise.reject('Comment text is empty');

    const commentData = {
      comment: editCommentText,
      case_id: caseId || null,
    };

    return updateTaskComment(taskId, id, commentData)
      .then(() => {
        // Update the comment in the local state
        setComments(comments.map((comment) => (comment.id === id ? { ...comment, text: editCommentText } : comment)));

        // Reset editing state
        setEditingComment(null);
        setEditCommentText('');

        return id;
      })
      .catch((error) => {
        console.error('Error updating comment:', error);
        throw error;
      });
  };

  /**
   * Deletes a comment after confirmation
   *
   * @param {string} id - The ID of the comment to delete
   * @returns {Promise<string>} Promise resolving to the ID of the deleted comment
   * @throws {Error} If the API call fails or the user cancels the deletion
   */
  const deleteComment = (id) => {
    return new Promise((resolve, reject) => {
      confirmDialog({
        message: 'Are you sure you want to delete this comment?',
        header: 'Delete Confirmation',
        icon: 'pi pi-info-circle',
        acceptClassName: 'p-button-danger',
        accept: () => {
          setIsDeletingComment(true);

          deleteTaskComment(taskId, id)
            .then(() => {
              // Remove the comment from the local state
              setComments(comments.filter((comment) => comment.id !== id));
              resolve(id);
            })
            .catch((error) => {
              console.error('Error deleting comment:', error);
              reject(error);
            })
            .finally(() => {
              setIsDeletingComment(false);
            });
        },
        reject: () => {
          reject('User cancelled deletion');
        },
      });
    });
  };

  /**
   * Handles scroll events for infinite scrolling of comments
   * Loads more comments when the user scrolls to the bottom of the list
   */
  const handleScroll = () => {
    if (!commentsListRef.current || !nextToken || isLoadingMoreComments) return;

    const { scrollTop, scrollHeight, clientHeight } = commentsListRef.current;

    // Check if scrolled to bottom (with a small threshold)
    if (scrollHeight - scrollTop - clientHeight < 50) {
      fetchComments(nextToken);
    }
  };

  // Load comments on initial render
  useEffect(() => {
    fetchComments();
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    comments,
    isAddingComment,
    isLoadingComments,
    isLoadingMoreComments,
    isDeletingComment,
    editingComment,
    editCommentText,
    commentsListRef,
    setEditCommentText,
    fetchComments,
    addComment,
    startEditing,
    updateComment,
    cancelEditing,
    deleteComment,
    handleScroll,
  };
};

export default useComments;
